import { useEffect } from 'react';
import { usePaymentStore } from '../state/usePaymentStore';
import { SubscriptionStatus } from '../types';
import { isStoreClient } from '@/lib/storeClient';

/**
 * ─── usePaymentGate ──────────────────────────────────────────────────────────
 *
 * L'UNIQUE hook que le reste de l'application utilise pour savoir si une
 * fonctionnalité doit être verrouillée derrière un abonnement.
 *
 * Règle d'or : `payments.enabled = false` → `isLocked` est TOUJOURS false.
 * L'app entière fonctionne alors normalement, aucun écran de paiement
 * n'apparaît, rien n'est bloqué.
 *
 * Usage :
 *   const { isLocked, paymentsEnabled } = usePaymentGate();
 *   if (isLocked) → afficher <PaymentGate /> ou rediriger vers /plans
 */
export interface PaymentGateState {
  /** Le module paiement est-il activé (config admin) ? */
  paymentsEnabled: boolean;
  /**
   * True uniquement sur le web (navigateur). Dans l'app store, FlexPay / checkout
   * sont masqués : l'abonnement s'achète sur le site.
   */
  canPurchaseInApp: boolean;
  /** L'utilisateur a-t-il un abonnement actif ? */
  hasActiveSubscription: boolean;
  /** L'abonnement existait mais a expiré (→ écran renouvellement). */
  isExpired: boolean;
  /** Faut-il verrouiller les fonctionnalités premium ? */
  isLocked: boolean;
  /** Jours restants avant expiration (null si pas d'abonnement). */
  daysRemaining: number | null;
  /**
   * Essais restants aujourd'hui selon les limites EFFECTIVES : quota gratuit
   * pour un non-abonné, entitlements du plan pour un abonné. Champ à null =
   * illimité pour ce type. L'objet entier est null quand rien n'est limité
   * (paiements OFF, ou plan entièrement illimité).
   */
  quota: {
    practiceRemaining: number | null;
    practiceLimit: number | null;
    simulationRemaining: number | null;
    simulationLimit: number | null;
  } | null;
}

export function usePaymentGate(): PaymentGateState {
  const config = usePaymentStore((s) => s.config);
  const subscription = usePaymentStore((s) => s.subscription);
  const refreshSubscriptionStatus = usePaymentStore((s) => s.refreshSubscriptionStatus);
  // Abonnés aux states usage/freeQuota/plans pour re-render quand un essai est
  // consommé ou que les limites changent (admin, hydratation serveur).
  usePaymentStore((s) => s.usage);
  usePaymentStore((s) => s.freeQuota);
  usePaymentStore((s) => s.plans);
  const effectiveLimits = usePaymentStore((s) => s.effectiveLimits);
  const remainingQuota = usePaymentStore((s) => s.remainingQuota);

  // Fait basculer ACTIVE → EXPIRED si la date est dépassée.
  useEffect(() => {
    refreshSubscriptionStatus();
  }, [refreshSubscriptionStatus]);

  if (!config.enabled) {
    return {
      paymentsEnabled: false,
      canPurchaseInApp: false,
      hasActiveSubscription: false,
      isExpired: false,
      isLocked: false,
      daysRemaining: null,
      quota: null,
    };
  }

  const canPurchaseInApp = !isStoreClient();

  const active = subscription?.status === SubscriptionStatus.ACTIVE;
  const expired = subscription?.status === SubscriptionStatus.EXPIRED;
  const daysRemaining =
    active && subscription
      ? Math.max(
          0,
          Math.ceil(
            (new Date(subscription.expiresAt).getTime() - Date.now()) /
              (24 * 60 * 60 * 1000),
          ),
        )
      : null;

  const limits = effectiveLimits();
  const fullyUnlimited =
    limits.dailyPracticeLimit === null && limits.dailySimulationLimit === null;

  return {
    paymentsEnabled: true,
    canPurchaseInApp,
    hasActiveSubscription: active,
    isExpired: expired,
    isLocked: !active,
    daysRemaining,
    quota: fullyUnlimited
      ? null
      : {
          practiceRemaining: remainingQuota('practice'),
          practiceLimit: limits.dailyPracticeLimit,
          simulationRemaining: remainingQuota('simulation'),
          simulationLimit: limits.dailySimulationLimit,
        },
  };
}
